import { MouseEvent, ReactElement, useCallback, useMemo, useState } from 'react'
import {
    AlbatrossTeamIcon,
    AppBar,
    EventIcon,
    FlagIcon,
    ForumIcon,
    GroupsIcon,
    ImportContactsIcon,
    MailIcon,
    MembersIcon,
    PeopleIcon,
    SayHiIcon,
    tss,
    useMediaQueries,
    Avatar,
} from '@ds/components'
import { DesktopMainMenu } from '@components/header/DesktopMainMenu'
import { DesktopMessagesMenu } from '@components/header/DesktopMessagesMenu'
import { DesktopTabBarFlyoutTrigger, TabBar, TabBarItem } from '@components/header/TabBar'
import { useDisabledStyles } from '@components/styles'
import { TCurrentUser } from '@types/common'
import { formatImageUrl } from '@utils/imageSizes'
import { MenuHeading, NavigationMenuItems } from '@components/header/NavigationMenuItems'
import { NavigationPromotion } from '@components/header/NavigationPromotion'
import { MenuLink, OffCanvasMenu } from '@components/header/OffCanvasMenu'
import { isBasicUser } from '@utils/members'

const useStyles = tss.create(() => ({
    rootBottom: {
        position: 'fixed',
        top: 'auto',
        bottom: 0,
    },
}))

export default function NavigationLoggedIn({
    position = 'top',
    currentUser,
}: {
    position?: 'top' | 'bottom'
    currentUser: TCurrentUser
}): ReactElement {
    const { classes, cx } = useStyles()
    const [isMenuOpen, setMenuOpen] = useState(false)
    const [flyoutTrigger, setFlyoutTrigger] = useState<null | HTMLElement>(null)

    const hardDunning = currentUser && currentUser.isInDunning // TODO ignored for this header >> && !currentUser?.permissions.canPayDunningLater.granted

    const { breakpointDependentValue } = useMediaQueries()

    const ref = breakpointDependentValue('ms_ul_mhb', 'he_pf_mem', '')
    const isDesktop = breakpointDependentValue(false, true, false)
    const isBasicMemberMobileWeb = !isDesktop && currentUser && isBasicUser(currentUser?.roles)
    const isBasicMemberWithContainerApp = currentUser && isBasicUser(currentUser.roles)
    const openMenu = useCallback(() => setMenuOpen(true), [])
    const closeMenu = useCallback(() => {
        setMenuOpen(false)
        setFlyoutTrigger(null)
    }, [])

    const navigationMenuItems = useMemo(() => {
        if (!currentUser) {
            return null
        }

        return (
            <NavigationMenuItems
                currentUser={currentUser}
                setFlyoutTrigger={setFlyoutTrigger}
                closeMenu={closeMenu}
                hardDunning={hardDunning}
            />
        )
    }, [closeMenu, currentUser, hardDunning])

    const memberAvatarOnClick = useCallback((event: MouseEvent<HTMLButtonElement>) => {
        setFlyoutTrigger(event.currentTarget)
    }, [])

    const { classes: disabledClasses } = useDisabledStyles()
    const disableNavigation = hardDunning
    const subscriptionRenewal = 'https://www.internations.org/subscription/renewal'
    const classToApply = cx(disableNavigation && disabledClasses.disabled)

    return (
        <AppBar className={cx(position === 'bottom' && classes.rootBottom)}>
            <TabBar
                localcommunityName={currentUser?.localcommunityName}
                onMenuOpen={openMenu}
                disableNavigation={false}
                isHardDunning={false}
            >
                <TabBarItem
                    className={classToApply}
                    Icon={FlagIcon}
                    route={hardDunning ? subscriptionRenewal : 'https://www.internations.org/start?ref=he_st'}
                    aria-disabled={disableNavigation}
                >
                    Start
                </TabBarItem>
                <TabBarItem
                    className={classToApply}
                    Icon={MembersIcon}
                    route={
                        hardDunning
                            ? subscriptionRenewal
                            : 'https://www.internations.org/members/recommendations?ref=he_me'
                    }
                    aria-disabled={disableNavigation}
                >
                    Members
                </TabBarItem>
                {isBasicMemberWithContainerApp || isBasicMemberMobileWeb ? (
                    <TabBarItem
                        className={classToApply}
                        Icon={AlbatrossTeamIcon}
                        route={
                            hardDunning
                                ? subscriptionRenewal
                                : 'https://www.internations.org/subscription?ref=subscriptionRefBasic&upgradeTrigger=ut:my_account/navigation'
                        }
                        showAlbatrossBottomNavIcon={true}
                    >
                        Albatross
                    </TabBarItem>
                ) : null}
                <TabBarItem
                    className={classToApply}
                    Icon={EventIcon}
                    route={hardDunning ? subscriptionRenewal : 'https://www.internations.org/events/mine#invitations'}
                    aria-disabled={disableNavigation}
                >
                    Events
                </TabBarItem>
                <TabBarItem
                    className={classToApply}
                    Icon={GroupsIcon}
                    route={hardDunning ? subscriptionRenewal : 'https://www.internations.org/activity-group?ref=he_gr'}
                    aria-disabled={disableNavigation}
                >
                    Groups
                </TabBarItem>

                <DesktopMessagesMenu className={classToApply} hardDunning={hardDunning} />
                <DesktopTabBarFlyoutTrigger onClick={memberAvatarOnClick}>
                    {currentUser == null ? null : (
                        <Avatar
                            src={currentUser.imagePath ? formatImageUrl(currentUser.imagePath, '32_32-3x') : undefined}
                            alt={currentUser.fullName}
                            size="small"
                        />
                    )}
                </DesktopTabBarFlyoutTrigger>
                <DesktopMainMenu
                    flyoutTrigger={flyoutTrigger}
                    setFlyoutTrigger={setFlyoutTrigger}
                    navigationMenuItems={navigationMenuItems}
                />

                {currentUser.isPremium ? <NavigationPromotion member={currentUser} /> : null}
            </TabBar>

            <OffCanvasMenu open={isMenuOpen} onClose={closeMenu}>
                {isBasicMemberWithContainerApp || isBasicMemberMobileWeb ? (
                    <MenuLink
                        Icon={PeopleIcon}
                        route="https://www.internations.org/groups?ref=he_gr"
                        onClick={closeMenu}
                    >
                        Groups
                    </MenuLink>
                ) : null}
                <MenuLink
                    Icon={MailIcon}
                    route={hardDunning ? subscriptionRenewal : ''} //'inbox.params({ ref: 'bm_msg' })'
                    // onClick={inboxOnClick}
                    onClick={() => {}}
                    className={classToApply}
                    aria-disabled={disableNavigation}
                >
                    Messages
                </MenuLink>
                <MenuLink
                    Icon={SayHiIcon}
                    route={hardDunning ? subscriptionRenewal : 'https://www.internations.org/sayhi?ref=bm_tw'}
                    onClick={closeMenu}
                    className={classToApply}
                    aria-disabled={disableNavigation}
                >
                    Members saying hi!
                </MenuLink>
                <MenuLink
                    Icon={ForumIcon}
                    route={hardDunning ? subscriptionRenewal : 'https://www.internations.org/forum?ref=he_fo'}
                    onClick={closeMenu}
                    aria-disabled={disableNavigation}
                >
                    Forum
                </MenuLink>
                <MenuLink
                    Icon={ImportContactsIcon}
                    route={hardDunning ? subscriptionRenewal : 'https://www.internations.org/forum?ref=he_gu'}
                    onClick={closeMenu}
                    aria-disabled={disableNavigation}
                >
                    Guide
                </MenuLink>

                <MenuHeading>{'profile & Network'}</MenuHeading>
                {navigationMenuItems}
            </OffCanvasMenu>
        </AppBar>
    )
}
